import { type UIMessage } from '@ai-sdk/react'
import {
  AnalyticsEvents,
  type OnboardingConversationGuidelineCapturedProperties,
  type OnboardingConversationViewedProperties,
} from '@mondaygpt/analytics-events'
import { PENDING_BUSINESS_ID } from '@mondaygpt/constants'
import { CreativeService } from 'openapi'
import { useAnalytics } from 'hooks'
import { fetchBrandKit } from 'modules/creative/api/brand-kit-api'
import { useBrandKitStore } from 'modules/creative/store/brand-kit-store'
import { type BrandMemoriesListHandle } from 'modules/creative/components/BrandMemoriesList'
import type { BrandKit } from 'modules/creative/types'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { BrandCardData } from './BrandCardStep'
import { ConversationInterview } from './ConversationInterview'
import { ConversationProgressHub } from './ConversationProgressHub'
import { INITIAL_INTERVIEW_CHROME, type InterviewChromeState } from './conversation-chrome'

export interface ConversationStepProps {
  /** The business the chat writes to. May be pending while it's being created. */
  businessId: string
  /** The scanned + confirmed brand card, used to ground the interview. */
  brandCard: BrandCardData
  /** Leave the interview (Skip on the opener / finished) and advance onboarding. */
  onDone?: () => void
}

/**
 * First-business conversational onboarding (desktop + mobile). Runs AFTER the
 * static BrandCardStep, where the user already saw + confirmed the scan result
 * (the non-chat "here's what we found" card). This step is a guided interview —
 * one grounded question at a time, each with a clear subject label and pick
 * options — that fills the gaps the card doesn't cover (post rules, voice
 * do's/don'ts, what to feature). On desktop a recap pane sits beside the chat
 * showing what's been captured so far; on mobile it's chat-only. It runs on the
 * existing `onboarding_conversation` intent; the one-question presentation + the
 * per-question subject are steered entirely by the seed below.
 */
export function ConversationStep({ businessId, brandCard, onDone }: ConversationStepProps) {
  const { track } = useAnalytics()
  const memoriesRef = useRef<BrandMemoriesListHandle>(null)
  const [liveKit, setLiveKit] = useState<BrandKit | null>(null)
  const [interviewChrome, setInterviewChrome] =
    useState<InterviewChromeState>(INITIAL_INTERVIEW_CHROME)
  const [listExpanded, setListExpanded] = useState(false)
  const [listFlash, setListFlash] = useState(false)
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [initialMessages, setInitialMessages] = useState<UIMessage[] | undefined>(undefined)
  const [chatLoaded, setChatLoaded] = useState(false)

  const businessReady = !!businessId && businessId !== PENDING_BUSINESS_ID

  // The confirm card is taller than the viewport, so the shell is usually
  // scrolled when we land here. Pin it back to the top so the interview isn't
  // hidden below the fold.
  useLayoutEffect(() => {
    const scroller = document.querySelector('main')?.parentElement as HTMLElement | null
    if (scroller) scroller.scrollTop = 0
  }, [])

  // Fire the step-viewed event once the chat is live (the business exists).
  const viewedRef = useRef(false)
  useEffect(() => {
    if (!businessReady || viewedRef.current) return
    viewedRef.current = true
    track(AnalyticsEvents.Onboarding.ONBOARDING_CONVERSATION_VIEWED, {
      business_id: businessId,
    } satisfies OnboardingConversationViewedProperties)
  }, [businessReady, businessId, track])

  // Hydrate the persisted onboarding thread before the interview seeds, so a
  // return into the chat resumes the transcript and a first entry (empty thread)
  // seeds. We fetch history first; but rather than gating the
  // render, we keep the warmup interview mounted and flip its `key` once loaded
  // (below) — useChat reads initialMessages only at construction, so the remount
  // is what injects the hydrated history.
  useEffect(() => {
    if (!businessReady || chatLoaded) return
    let cancelled = false
    CreativeService.creativeControllerGetOnboardingChatV1()
      .then((data: any) => {
        if (cancelled) return
        if (data?.messages?.length) {
          setInitialMessages(
            data.messages.map((m: any) => ({
              id: m.id,
              role: m.role,
              parts: m.parts,
            })) as UIMessage[]
          )
        }
        setChatLoaded(true)
      })
      .catch(err => {
        console.error('[ConversationStep] onboarding chat history load failed:', err)
        if (!cancelled) setChatLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [businessReady, chatLoaded])

  // Guideline list count drives capture analytics; peek fires on tool activity
  // (milliseconds before the next question) and again when the list refreshes.
  const triggerSavePeek = useCallback(() => {
    setListExpanded(true)
    setListFlash(true)
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current)
    peekTimerRef.current = setTimeout(() => {
      setListExpanded(false)
      setListFlash(false)
    }, 1600)
  }, [])

  const prevCountRef = useRef<number | null>(null)
  const handleCountChange = useCallback(
    (count: number) => {
      if (prevCountRef.current !== null && count > prevCountRef.current) {
        track(AnalyticsEvents.Onboarding.ONBOARDING_CONVERSATION_GUIDELINE_CAPTURED, {
          business_id: businessId,
          total_count: count,
        } satisfies OnboardingConversationGuidelineCapturedProperties)
      }
      prevCountRef.current = count
    },
    [businessId, track]
  )

  useEffect(() => {
    return () => {
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current)
    }
  }, [])

  // Any agent tool result may have captured a memory OR patched the brand kit —
  // re-pull the guideline list AND the brand kit so the capture analytics fires
  // and post previews reflect any edit immediately.
  const handleToolActivity = useCallback(() => {
    triggerSavePeek()
    memoriesRef.current?.refresh()
    fetchBrandKit()
      .then(kit => {
        setLiveKit(kit)
        // Keep the GLOBAL brand-kit store in sync too. AppLayout only refetches
        // it on business switch, which never fires mid-onboarding — so without
        // this, anything the user changes here is invisible to post previews.
        useBrandKitStore.getState().setBrandKitFromApi(kit)
      })
      .catch(err => console.warn('[ConversationStep] brand kit refetch failed:', err))
  }, [triggerSavePeek])

  const brandName = (liveKit?.businessName || brandCard.businessName)?.trim() || 'your brand'

  // What the scan already gave us vs. what's still thin/missing — injected into
  // the seed so the agent targets gaps instead of asking about things we know.
  const card = {
    description: liveKit?.description ?? brandCard.description,
    targetAudience: liveKit?.targetAudience ?? brandCard.targetAudience,
    tones: liveKit?.toneOfVoice ?? brandCard.tones,
    keywords: liveKit?.keywords ?? brandCard.keywords,
    contentFocus: liveKit?.contentFocus ?? brandCard.contentFocus,
    images: liveKit?.images ?? brandCard.images,
  }
  const known: string[] = []
  const missing: string[] = []
  const note = (label: string, present: boolean) => (present ? known : missing).push(label)
  note('what you do', !!card.description?.trim())
  note('who you serve (audience)', !!card.targetAudience?.trim())
  note('your tone of voice', (card.tones?.length ?? 0) > 0)
  note('topics & keywords', (card.keywords?.length ?? 0) > 0)
  note('what to feature in posts', !!card.contentFocus)
  note('brand photos', (card.images?.length ?? 0) > 0)

  // Hidden seed (first user message). The DIRECTIVE holds the durable rules, but
  // the seed RESTATES the load-bearing, brittle ones (the [[subject:]]/[[options:]]
  // format, scan-grounding, post-focus, no-aesthetic) because their RECENCY as the
  // last message is what makes the model actually comply — gutting this regressed
  // marker format + grounding live (verified 2026-06-22). It also injects the
  // per-brand scan data the directive can't. Keep both in sync.
  const seedMessage = `I'm ${brandName}. I've already reviewed and confirmed my brand details (name, industry, audience, tone, colours, look) on the previous screen — they're correct. Do NOT re-list what you found, and do NOT ask me to confirm my details again.

This chat is ABOUT MY POSTS — how to post me, and what's missing from my posting today. It is NOT brand discovery. Every question is a POST question: what to post, what each post should show, what captions should say or avoid, what kind of posts I want more of. Do NOT ask about my brand in the abstract — no origin story, no founders, no "why customers choose you", no personality/values/aesthetic. Before each question, finish "this changes the post by ___" (topic/image/caption/hashtags); if you can't, it's a brand question — don't ask it.

Make this feel like a friendly CHAT, not a survey. Your FIRST message must BE a question — open DIRECTLY with your first gap question in the full step format below (a [[subject:]] heading + the question + [[options:]]). Do NOT send a separate hello, greeting, or compliment bubble before it, do NOT introduce yourself as an assistant, and do NOT open with "let's…" filler. BANNED: "creative assistant", "AI assistant", "I'm ready to", "help you", "make your posts shine/pop/compelling", and marketing words like "amazing"/"unique"/"truly". A tiny warm word fused INTO the question is fine, but the first thing I see must be a real question, not a greeting. No "let's start" gate, no waiting.

MY SCAN (read this — your questions must be built from it): ${card.description?.trim() ? `"${card.description.trim()}"` : '(thin — lean on the profile above)'}${card.targetAudience?.trim() ? ` My audience: "${card.targetAudience.trim()}".` : ''}
Your FIRST question + its options must pull SPECIFICS straight out of that scan sentence (what I make, where, my materials, named people/places) so I instantly feel read. E.g. if it says "designed in Tel Aviv, handmade in Portugal", open on that — never a blank "What's your top focus?" with generic chips.

Here's what you ALREADY have from the scan: ${known.join(', ') || 'the basics'}.
Still thin or missing: ${missing.join(', ') || 'just the finer details'}.

Run a focused interview whose LENGTH DEPENDS ON ME — not a fixed number. Ask one question per real, high-value POST gap, tuned to (a) how much you already know about me and (b) my business type. I have ${known.length || 'a few'} basics already and about ${missing.length || 'a couple of'} areas still thin — so aim for roughly that many focused questions: ask FEWER (≈3) if the scan already covers a lot, MORE (up to ≈6) if I'm thin or there's a lot to mine for a type like mine. Never pad to a number; never stop while a real post gap is open; don't jump to photos after just 1–2. Easiest+most concrete gap FIRST (never open with the abstract "what makes you special"), ONE topic at a time, talking like a person. Do NOT camp on voice + look. Every topic is a POST topic — come away with (skip any we clearly already have): what ${brandName} should POST ABOUT → caption subject (2–3 kinds of posts, not the bucket); what every post should FEATURE → recurring product/person/signature (the options carry the value); what every post should SHOW → the image (a setting/element/named place — NEVER a feeling or aesthetic label like "relaxed chic"/"minimalist vibe"); what captions should SAY / never say → wording (a signature phrase or real don't — skip the obvious like "never say cheap"); and what's MISSING from my posts today → a kind of post I wish I made more of, or what my posts currently lack (e.g. "more behind-the-scenes", "show real customers"). Do NOT ask brand-discovery questions — no origin story, founders, "why people choose me", mission/values/personality. If a brand fact matters, ask it as a post question ("should posts mention you're family-run?"), never as trivia.

BANNED topics — do NOT ask these: (1) GOALS / what posts should achieve / drive / a call-to-action (online orders vs store visits vs awareness, "Shop Now", etc.) — the very NEXT onboarding step asks my goals, so asking here double-asks; stay on brand substance, not objectives. (2) "What feeling/vibe should your posts give?" / "How do you make people feel <X>?" — a mood is not post data; ask what to SHOW or SAY instead.

PROVE YOU READ MY SITE — this is the whole point. You already have a rich scan of ${brandName} above (what I make, where, my materials, named people/places, audience). USE those specifics out loud so I feel you actually know me. Your FIRST question + its options must be BUILT FROM the scan description of ${brandName} (what I make, where it's made, a material, a founder) — pull those specifics straight in. FORBIDDEN openers: a blank "What do you post about most?", and a generic "what hero offering should we spotlight?" with placeholder chips like [Signature dish | Craftsmanship | Unique experience]. NEVER use food words (dish, menu, recipe, flavor) unless I'm actually a food business — match my real category. Anchor EVERY question to a real product/place/detail, not my name slotted into a generic stem.

The [[options:]] are where grounding lives — they MUST be real specifics from ${brandName}'s profile (product lines, materials, places, techniques, people), each ≤4 words. Generic buckets are BANNED ("New products | Behind scenes | Customer stories", "Comfort | Unique design | Quality materials", "Inspired | Indulgent | Sophisticated") — they fit any business and prove I wasn't read. Example for a Tel-Aviv-designed, Portugal-made label: ❌ "What makes your sandals special?" [Comfort | Unique design | Quality materials] → ✅ "Designed in Tel Aviv, made in Portugal — which leads your posts?" [The Tel Aviv design | The Portugal craft | The Japanese fabrics]. I can always type my own.

Each NEW STEP: the [[subject:]] IS the question — SHORT and CASUAL, talk TO me in second person (you/your) like texting a friend, NOT a formal survey (aim for ≤10 words). It renders as the heading. Official phrasing is BAD ("Why do businesses choose ${brandName}?"); casual is GOOD ("What do people pick you for?"). No "businesses/customers/resonates/leverage":
- [[subject: <the short, casual question>]] (e.g. [[subject: What do you post about most?]], [[subject: What's a line you'd never use?]]). Put the WHOLE question INSIDE the marker — never an empty [[subject:]] with the text outside it. Ask it the way you'd say it out loud — no "payoff" label.
- NO extra line under it — the question stands alone (an explainer bubble makes it a wall of text). Omit it.
- Last line: EXACTLY 3 quick-reply options, each ≤4 words, concrete and grounded in ${brandName}: [[options: first | second | third]].

The final two steps are FIXED, in this order (gap questions come first, then these):
- SECOND-TO-LAST step — photos: [[subject: Got a few photos to share?]], then a one-line invite to attach them right here in the chat (photo button or drag-drop). NO [[options:]]. Photos I attach are saved to my brand kit automatically.
- LAST step is open-ended: [[subject: Anything else I should know?]] — no extra line needed, NO [[options:]].

IMPORTANT: only use [[subject: ...]] / [[options: ...]] when you OPEN A BRAND-NEW STEP. If I ask you to clarify or rephrase, or you're just replying conversationally, answer in plain sentences with NO markers — and do NOT re-open the step. When I attach photos, just acknowledge warmly and move on.

CAPTURE EVERY ANSWER (mandatory): call captureBrandMemory for EVERY answer I give — a tapped chip, a typed reply, AND a follow-up reply all count. You may NOT open the next [[subject:]] until you've captured the answer to the current one. A tapped chip like "Signature dress" is me teaching you my hero product — capture it, don't just react and move on. An answer you don't capture is lost forever and never reaches a post. Never capture a mood/feeling ("chic vibe") or a goal ("grow audience") — those don't help write a post. DISTILL into a short bullet in your words but KEEP the specific (a name, product, place, number).

This is an INTERVIEW, not a form — DIG before you advance (most important rule). Each topic takes TWO of your turns whenever my answer has any hook (a noun, number, opinion, example — almost always):
- Turn A (dig): capture it FIRST (distill, keep the specific), then ONE sharp follow-up question pulling out a concrete detail (e.g. I say "a little playful" → "Playful how — a line that felt very you?"). An ≤8-word reaction naming a concrete noun I said is OPTIONAL — when in doubt, skip it and just ask. This turn has NO [[subject:]] and NO [[options:]] — you may NOT open a new subject here.
- Turn B (advance): after I answer the follow-up, a few-word reaction, then open the NEXT subject — the question itself in [[subject: ...]] (short + casual; renders as the heading; EVERY new step needs one — gap questions, PHOTOS, last step) + (an optional extra line only if needed) + [[options:]].
Skip the follow-up only if my answer was already concrete, or thin/I'm done. When unsure, DIG. Turns without [[subject:]] are only reactions/follow-ups on the current topic. Reactions are OPTIONAL — default to NONE and just ask. If you react, it MUST be a concrete echo of the form "<a noun/detail I said> — <≤3-word concrete note>." with ZERO approval words (good: "Macarons daily — noted.", "Ispahan — rose and lychee."). FORBIDDEN — any praise reaction, i.e. anything starting with or containing: Great/Fantastic/Perfect/Nice/Lovely/Amazing/Excellent/Love it/I love that/Totally/Understood/Got it/Okay/Thanks for sharing/"<noun> it is!". If you can't form the concrete echo, write NO reaction and open with the question. Never repeat a step. When you ask the FINAL step ("Anything else"), call the completeInterview tool on that same turn (NOT after my reply — I may have nothing to add) so my Continue button appears. Call it exactly once. If I DO add a final note, FIRST captureBrandMemory anything useful in it (never drop my last answer), then thank me briefly.

I answer by tapping an option, typing, or attaching photos.`

  return (
    <div className="mx-auto -mt-12 mb-[-6rem] flex h-[calc(100dvh-3rem)] w-full max-w-[440px] flex-col lg:mt-0 lg:mb-0 lg:h-[calc(100dvh-9rem)] lg:max-w-[900px]">
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row lg:items-stretch lg:gap-10">
        <div className="hidden min-h-0 lg:flex lg:flex-1 lg:justify-center">
          <ConversationProgressHub
            chrome={interviewChrome}
            memoriesRef={memoriesRef}
            businessReady={businessReady}
            listExpanded={listExpanded}
            onToggleList={() => setListExpanded(e => !e)}
            listFlash={listFlash}
            onCountChange={handleCountChange}
            onSkip={onDone}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:w-[440px] lg:flex-none lg:overflow-hidden lg:rounded-[14px] lg:border lg:border-line lg:bg-bg-2">
          <ConversationInterview
            key={chatLoaded ? 'live' : 'warmup'}
            intent="onboarding_conversation"
            chatId="onboarding-conversation-chat"
            persist
            initialMessages={initialMessages}
            seedOnMount={businessReady && chatLoaded}
            brandName={brandName}
            seedMessage={seedMessage}
            onToolActivity={handleToolActivity}
            onSkip={onDone}
            onInterviewStateChange={setInterviewChrome}
          />
        </div>
      </div>
    </div>
  )
}
